import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, Building2, Users, Briefcase, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const directorates = [{
  name: 'Directorate of Crop Resources',
  head: 'Commissioner for Crop Production',
  link: '/about/crop-resources',
  functions: ['Crop production and productivity', 'Crop protection and quarantine', 'Seed certification and quality assurance']
}, {
  name: 'Directorate of Animal Resources',
  head: 'Commissioner for Animal Production',
  link: '/about/animal-resources',
  functions: ['Livestock production and marketing', 'Animal health and disease control', 'Veterinary regulation and inspection']
}, {
  name: 'Directorate of Fisheries Resources',
  head: 'Commissioner for Fisheries',
  link: '/about/fisheries-resources',
  functions: ['Fisheries management and regulation', 'Aquaculture development', 'Fish quality assurance']
}, {
  name: 'Directorate of Agricultural Extension Services',
  head: 'Commissioner for Agricultural Extension',
  link: '/about/extension-services',
  functions: ['Agricultural advisory services', 'Technology transfer', 'Farmer group development']
}];
const departments = ['Finance and Administration', 'Planning and Development', 'Agricultural Infrastructure and Water for Production', 'Farm Development', 'Entomology', 'Crop Inspection and Certification'];
export function MinistryStructurePage() {
  return <>
      <Header data-id="element-1119" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-1120">
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-1121">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1600&h=600&fit=crop")'
        }} data-id="element-1122"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-1123"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-1124">
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-1125">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-1126">
                <Home className="w-4 h-4 mr-1" data-id="element-1127" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1128" />
              <span data-id="element-1129">About MAAIF</span>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1130" />
              <span className="text-white font-medium" data-id="element-1131">Ministry Structure</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1132">
              Ministry Structure
            </h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed" data-id="element-1133">
              The organizational structure of the Ministry of Agriculture,
              Animal Industry and Fisheries, comprising four technical
              directorates and supporting departments.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-1134">
          {/* Top Leadership */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12" data-id="element-1135">
            <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-1136">
              Top Leadership
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1137">
              {[{
              role: 'Minister',
              name: 'Hon. Frank Tumwebaze',
              desc: 'Political head of the Ministry responsible for policy direction.'
            }, {
              role: 'Minister of State for Agriculture',
              name: 'Hon. Fred Bwino Kyakulaga',
              desc: 'Assists the Minister on crop and agricultural matters.'
            }, {
              role: 'Permanent Secretary',
              name: 'Maj. Gen. David Kasura-Kyomukama',
              desc: 'Chief accounting officer and administrative head of the Ministry.'
            }].map((leader, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-1138">
                  <div className="w-12 h-12 rounded-full bg-uganda-green text-white flex items-center justify-center mb-4" data-id="element-1139">
                    <Users className="w-6 h-6" data-id="element-1140" />
                  </div>
                  <p className="text-xs font-bold text-uganda-green uppercase tracking-wider mb-1" data-id="element-1141">
                    {leader.role}
                  </p>
                  <h3 className="text-lg font-bold text-uganda-black mb-2" data-id="element-1142">
                    {leader.name}
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-1143">{leader.desc}</p>
                </motion.div>)}
            </div>
          </div>

          {/* Directorates */}
          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-1144">
            Technical Directorates
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-1145">
            {directorates.map((dir, index) => <motion.div key={dir.name} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow" data-id="element-1146">
                <div className="w-12 h-12 rounded-xl bg-green-50 text-uganda-green flex items-center justify-center mb-4" data-id="element-1147">
                  <Building2 className="w-6 h-6" data-id="element-1148" />
                </div>
                <h3 className="text-xl font-bold text-uganda-black mb-1" data-id="element-1149">
                  {dir.name}
                </h3>
                <p className="text-sm text-gray-500 mb-4" data-id="element-1150">
                  Headed by: {dir.head}
                </p>
                <ul className="space-y-2 mb-5" data-id="element-1151">
                  {dir.functions.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-1152">
                      <span className="w-1.5 h-1.5 bg-uganda-green rounded-full mt-1.5 flex-shrink-0" data-id="element-1153" />
                      {f}
                    </li>)}
                </ul>
                <Link to={dir.link} className="inline-flex items-center gap-1 text-sm font-semibold text-uganda-green hover:text-green-800 transition-colors" data-id="element-1154">
                  Learn More <ArrowRight className="w-4 h-4" data-id="element-1155" />
                </Link>
              </motion.div>)}
          </div>

          {/* Support Departments */}
          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-1156">
            Support Departments
          </h2>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1157">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1158">
              {departments.map((dept, i) => <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50" data-id="element-1159">
                  <Briefcase className="w-5 h-5 text-uganda-green flex-shrink-0" data-id="element-1160" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-1161">
                    {dept}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-1162" />
    </>;
}